import React, { useCallback, useEffect, useState } from 'react';
import { authAPI, employeeAdminAPI } from '../services/api';
import type { NclEmployee } from '../services/api';
import { formatApiError, PASSWORD_REQUIREMENTS_HINT } from '../utils/apiErrors';

const DEFAULT_SICK_HOURS = '48';

function todayInputValue(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function displayName(employee: NclEmployee): string {
  const name = `${employee.first_name} ${employee.last_name}`.trim();
  return name || employee.username;
}

const emptyForm = () => ({
  firstName: '',
  lastName: '',
  username: '',
  password: '',
  passwordConfirm: '',
  hourlyRate: '',
  effectiveDate: todayInputValue(),
  sickHours: DEFAULT_SICK_HOURS,
});

const AdminEmployees: React.FC = () => {
  const [employees, setEmployees] = useState<NclEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [resetUserId, setResetUserId] = useState<number | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetting, setResetting] = useState(false);
  const [statusUserId, setStatusUserId] = useState<number | null>(null);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await employeeAdminAPI.list();
      setEmployees(response.data);
    } catch (err: unknown) {
      setError(formatApiError(err, 'Failed to load employees.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const updateForm = (field: keyof ReturnType<typeof emptyForm>, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (form.password !== form.passwordConfirm) {
      setError('Passwords do not match.');
      return;
    }
    setCreating(true);
    try {
      await employeeAdminAPI.create({
        username: form.username.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        password: form.password,
        password_confirm: form.passwordConfirm,
        hourly_rate: form.hourlyRate,
        effective_date: form.effectiveDate,
        sick_hours: form.sickHours,
      });
      setSuccess(
        `Added ${form.firstName.trim()} ${form.lastName.trim()}. ` +
          'Give them the temporary password; they must change it on login.'
      );
      setForm(emptyForm());
      await loadEmployees();
    } catch (err: unknown) {
      setError(formatApiError(err, 'Failed to add employee.'));
    } finally {
      setCreating(false);
    }
  };

  const handleReset = async (event: React.FormEvent, employee: NclEmployee) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (resetPassword !== resetConfirm) {
      setError('Passwords do not match.');
      return;
    }
    setResetting(true);
    try {
      await authAPI.resetUserPassword(employee.id, {
        new_password: resetPassword,
        new_password_confirm: resetConfirm,
      });
      setSuccess(
        `Password reset for ${employee.username}. Give them the temporary password; ` +
          'they will be prompted to change it on login.'
      );
      setResetUserId(null);
      setResetPassword('');
      setResetConfirm('');
    } catch (err: unknown) {
      setError(formatApiError(err, 'Failed to reset password.'));
    } finally {
      setResetting(false);
    }
  };

  const handleDeactivate = async (employee: NclEmployee) => {
    const confirmed = window.confirm(
      `Deactivate ${displayName(employee)}? They will not be able to log in. ` +
        'Their past time and payroll history stay.'
    );
    if (!confirmed) {
      return;
    }
    setStatusUserId(employee.id);
    setError('');
    setSuccess('');
    try {
      await employeeAdminAPI.deactivate(employee.id);
      setSuccess(`${displayName(employee)} is deactivated.`);
      await loadEmployees();
    } catch (err: unknown) {
      setError(formatApiError(err, 'Failed to deactivate employee.'));
    } finally {
      setStatusUserId(null);
    }
  };

  const handleReactivate = async (employee: NclEmployee) => {
    setStatusUserId(employee.id);
    setError('');
    setSuccess('');
    try {
      await employeeAdminAPI.reactivate(employee.id);
      setSuccess(`${displayName(employee)} can log in again.`);
      await loadEmployees();
    } catch (err: unknown) {
      setError(formatApiError(err, 'Failed to reactivate employee.'));
    } finally {
      setStatusUserId(null);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h2>Add Employee</h2>
        <p style={{ color: '#555', marginBottom: '1.5rem' }}>
          Create a username and temporary password. The employee must change it on first login.
          Sick hours default to 48; enter less for a mid-year start. Vacation stays at 0 until you set it on Pay Rates.
        </p>
        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}
        <form onSubmit={handleCreate}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <div className="form-group">
              <label htmlFor="emp-first-name">First name</label>
              <input id="emp-first-name" value={form.firstName} onChange={(e) => updateForm('firstName', e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="emp-last-name">Last name</label>
              <input id="emp-last-name" value={form.lastName} onChange={(e) => updateForm('lastName', e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="emp-username">Username</label>
              <input id="emp-username" value={form.username} onChange={(e) => updateForm('username', e.target.value)} required autoComplete="off" />
            </div>
            <div className="form-group">
              <label htmlFor="emp-rate">Hourly rate</label>
              <input id="emp-rate" type="number" min="0.01" step="0.01" value={form.hourlyRate} onChange={(e) => updateForm('hourlyRate', e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="emp-effective">Rate effective date</label>
              <input id="emp-effective" type="date" value={form.effectiveDate} onChange={(e) => updateForm('effectiveDate', e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="emp-sick">Sick hours this year</label>
              <input id="emp-sick" type="number" min="0" step="0.01" value={form.sickHours} onChange={(e) => updateForm('sickHours', e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="emp-password">Temporary password</label>
              <input id="emp-password" type="password" value={form.password} onChange={(e) => updateForm('password', e.target.value)} required autoComplete="new-password" />
            </div>
            <div className="form-group">
              <label htmlFor="emp-password-confirm">Confirm temporary password</label>
              <input id="emp-password-confirm" type="password" value={form.passwordConfirm} onChange={(e) => updateForm('passwordConfirm', e.target.value)} required autoComplete="new-password" />
            </div>
          </div>
          <small style={{ color: '#666', display: 'block', marginBottom: '1rem' }}>{PASSWORD_REQUIREMENTS_HINT}</small>
          <button type="submit" className="btn btn-primary" disabled={creating}>
            {creating ? 'Adding...' : 'Add Employee'}
          </button>
        </form>
      </div>

      <div className="card">
        <h2>Employees</h2>
        <p style={{ color: '#555', marginBottom: '1rem' }}>
          Deactivated employees cannot log in and drop out of time and pay-rate lists. Past punches and payroll letters stay.
        </p>
        {loading ? (
          <p>Loading employees...</p>
        ) : employees.length === 0 ? (
          <p>No employees yet.</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => (
                  <React.Fragment key={employee.id}>
                    <tr>
                      <td>{displayName(employee)}</td>
                      <td>{employee.username}</td>
                      <td>{employee.is_active ? 'Active' : 'Inactive'}</td>
                      <td>
                        {employee.is_active ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-small btn-secondary"
                              style={{ marginRight: '0.5rem' }}
                              onClick={() => {
                                setResetUserId(resetUserId === employee.id ? null : employee.id);
                                setResetPassword('');
                                setResetConfirm('');
                                setError('');
                              }}
                            >
                              Reset password
                            </button>
                            <button
                              type="button"
                              className="btn btn-small btn-danger"
                              disabled={statusUserId === employee.id}
                              onClick={() => handleDeactivate(employee)}
                            >
                              Deactivate
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-small btn-secondary"
                            disabled={statusUserId === employee.id}
                            onClick={() => handleReactivate(employee)}
                          >
                            Reactivate
                          </button>
                        )}
                      </td>
                    </tr>
                    {resetUserId === employee.id && employee.is_active && (
                      <tr>
                        <td colSpan={4}>
                          <form onSubmit={(event) => handleReset(event, employee)} style={{ maxWidth: '480px' }}>
                            <div className="form-group">
                              <label htmlFor={`reset-password-${employee.id}`}>Temporary password</label>
                              <input
                                id={`reset-password-${employee.id}`}
                                type="password"
                                value={resetPassword}
                                onChange={(e) => setResetPassword(e.target.value)}
                                required
                                autoComplete="new-password"
                              />
                              <small style={{ color: '#666', display: 'block', marginTop: '0.35rem' }}>
                                {PASSWORD_REQUIREMENTS_HINT}
                              </small>
                            </div>
                            <div className="form-group">
                              <label htmlFor={`reset-confirm-${employee.id}`}>Confirm temporary password</label>
                              <input
                                id={`reset-confirm-${employee.id}`}
                                type="password"
                                value={resetConfirm}
                                onChange={(e) => setResetConfirm(e.target.value)}
                                required
                                autoComplete="new-password"
                              />
                            </div>
                            <button type="submit" className="btn btn-primary btn-small" disabled={resetting}>
                              {resetting ? 'Resetting...' : 'Set temporary password'}
                            </button>
                          </form>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmployees;
